import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
// @ts-expect-error Legacy JavaScript module has no type declarations.
import { selectIsAdmin } from '@/features/Profile/profile.selectors'
import { useStartBillingRestriction } from '../api/start-billing-restriction'
import { useStopBillingRestriction } from '../api/stop-billing-restriction'
import {
	BillingDelinquencyInfo,
	START_WORKFLOW_CONFIRMATION,
	STOP_WORKFLOW_CONFIRMATION,
	getCustomerAccessLabel,
	getStartWorkflowErrorMessage,
	getStopWorkflowErrorMessage,
	getWorkflowStatusLabel,
	isBalancePaidWhileWorkflowActive,
} from '../billingRestriction'

const formatDateTime = (value?: string | null) =>
	value ? new Date(value).toLocaleString() : '—'

type AccountBillingStatusProps = {
	accountId: string
	billingDelinquency?: BillingDelinquencyInfo | null
}

type PendingToggle = 'on' | 'off' | null

export default function AccountBillingStatus({ accountId, billingDelinquency }: AccountBillingStatusProps) {
	const isAdmin = useSelector(selectIsAdmin)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [pendingToggle, setPendingToggle] = useState<PendingToggle>(null)
	const startMutation = useStartBillingRestriction({
		mutationConfig: {
			onSuccess: () => setErrorMessage(null),
			onError: (error) => setErrorMessage(getStartWorkflowErrorMessage(error)),
		},
	})
	const stopMutation = useStopBillingRestriction({
		mutationConfig: {
			onSuccess: () => setErrorMessage(null),
			onError: (error) => setErrorMessage(getStopWorkflowErrorMessage(error)),
		},
	})

	const info = billingDelinquency
	const isWorkflowOn = Boolean(info?.canStopWorkflow)
	// Accounts without an account-level Chargebee subscription can't pay in 4473 Cloud, so a workflow
	// can't be started for them. A running workflow still shows the toggle so it can be turned off.
	const canOfferStart = info?.isPendingReview && info?.isSelfServeBillingEligible !== false
	const showToggle = Boolean(isAdmin && (canOfferStart || isWorkflowOn))
	const isToggleDisabled =
		startMutation.isPending || stopMutation.isPending || (!isWorkflowOn && !info?.canStartWorkflow)

	const confirmToggle = () => {
		if (pendingToggle === 'on') startMutation.mutate({ accountId })
		if (pendingToggle === 'off') stopMutation.mutate({ accountId })
		setPendingToggle(null)
	}

	return (
		<Card className='h-full'>
			<CardHeader>
				<CardTitle>Billing Status</CardTitle>
			</CardHeader>
			<CardContent className='space-y-4'>
				<div className='flex flex-wrap gap-2'>
					{info?.hasOutstandingBalance ? (
						<Badge variant='warning'>Outstanding balance</Badge>
					) : (
						<Badge variant='outline'>No outstanding balance</Badge>
					)}
					{info?.isPendingReview && <Badge variant='destructive'>Flagged</Badge>}
					{info?.isActivationExcluded && <Badge variant='outline'>Excluded from activation</Badge>}
				</div>

				<div className='grid gap-4 sm:grid-cols-2'>
					<div>
						<div className='text-xs uppercase tracking-wide text-muted-foreground'>Balance</div>
						{info?.chargebeeSubscriptionUrl ? (
							<a
								href={info.chargebeeSubscriptionUrl}
								target='_blank'
								rel='noopener noreferrer'
								className='text-sm break-all text-primary hover:underline'
							>
								View balance in Chargebee
							</a>
						) : (
							<div className='text-sm'>—</div>
						)}
					</div>
					<div>
						<div className='text-xs uppercase tracking-wide text-muted-foreground'>Last confirmed</div>
						<div className='text-sm'>{formatDateTime(info?.lastConfirmedAt)}</div>
					</div>
					<div>
						<div className='text-xs uppercase tracking-wide text-muted-foreground'>Workflow</div>
						<div className='text-sm'>{getWorkflowStatusLabel(info)}</div>
						{info?.workflowStartedAt && (
							<div className='text-xs text-muted-foreground'>
								Started {formatDateTime(info.workflowStartedAt)}
							</div>
						)}
					</div>
					<div>
						<div className='text-xs uppercase tracking-wide text-muted-foreground'>Customer access</div>
						<div className='text-sm'>{getCustomerAccessLabel(info?.customerAccess)}</div>
					</div>
				</div>

				{isBalancePaidWhileWorkflowActive(info) && (
					<Badge variant='warning'>Balance paid - turn off to restore access</Badge>
				)}

				{showToggle && (
					<div className='space-y-2'>
						<div className='flex items-center justify-between border rounded-md p-2'>
							<div>
								<div className='text-sm font-medium'>Delinquency notifications</div>
								<div className='text-xs text-muted-foreground'>
									{isWorkflowOn
										? 'Emails and access restrictions run on schedule until turned off.'
										: 'Turn on to start customer emails and scheduled access restrictions.'}
								</div>
							</div>
							<Switch
								aria-label='Delinquency notifications'
								checked={isWorkflowOn}
								disabled={isToggleDisabled}
								title={!isWorkflowOn ? info?.startBlockedReason ?? undefined : undefined}
								onCheckedChange={(checked) => setPendingToggle(checked ? 'on' : 'off')}
							/>
						</div>
						{!isWorkflowOn && !info?.canStartWorkflow && info?.startBlockedReason && (
							<div className='text-xs text-muted-foreground'>{info.startBlockedReason}</div>
						)}
					</div>
				)}
				{errorMessage && <div className='text-sm text-destructive'>{errorMessage}</div>}

				<AlertDialog open={pendingToggle !== null} onOpenChange={(open) => !open && setPendingToggle(null)}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{pendingToggle === 'off'
									? 'Turn off delinquency notifications?'
									: 'Turn on delinquency notifications?'}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{pendingToggle === 'off' ? STOP_WORKFLOW_CONFIRMATION : START_WORKFLOW_CONFIRMATION}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<AlertDialogAction onClick={confirmToggle}>
								{pendingToggle === 'off' ? 'Turn off' : 'Turn on'}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</CardContent>
		</Card>
	)
}
